import styled from 'styled-components'

export const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px 80px;
  min-height: 80vh;
`

export const Header = styled.div`
  margin-bottom: 40px;

  h1 {
    font-size: 2rem;
    margin-bottom: 8px;
    color: ${({ theme }) => theme.colors.textPrimary};
    display: flex;
    align-items: center;
    gap: 12px;

    svg {
      color: ${({ theme }) => theme.colors.primary};
    }
  }

  p {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
`

export const StatCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 16px;

  .icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ $color }) => $color}20;
    color: ${({ $color }) => $color};

    svg {
      width: 24px;
      height: 24px;
    }
  }

  .info {
    h3 {
      font-size: 1.8rem;
      font-weight: 700;
      color: ${({ theme }) => theme.colors.textPrimary};
      line-height: 1;
    }

    p {
      font-size: 0.9rem;
      color: ${({ theme }) => theme.colors.textSecondary};
      margin-top: 4px;
    }
  }
`

export const Section = styled.section`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  margin-bottom: 24px;
  overflow: hidden;
`

export const SectionHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;

  h2 {
    font-size: 1.2rem;
    color: ${({ theme }) => theme.colors.textPrimary};
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
      color: ${({ theme }) => theme.colors.primary};
    }
  }
`

export const SearchInput = styled.input`
  padding: 10px 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.9rem;
  min-width: 350px;

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 16px 24px;
    text-align: left;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }

  th {
    font-size: 0.85rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textSecondary};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  td {
    font-size: 0.95rem;
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover {
    background: ${({ theme }) => theme.colors.surfaceElevated};
  }

  @media (max-width: 768px) {
    display: block;
    overflow-x: auto;
  }
`

export const Badge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;

  ${({ $variant }) => {
    switch ($variant) {
      case 'success':
        return `
          background: #10b98120;
          color: #10b981;
        `
      case 'warning':
        return `
          background: #f59e0b20;
          color: #f59e0b;
        `
      case 'danger':
        return `
          background: #ef444420;
          color: #ef4444;
        `
      case 'info':
        return `
          background: #6464ff20;
          color: #6464ff;
        `
      default:
        return `
          background: #6b728020;
          color: #6b7280;
        `
    }
  }}
`

export const ActionButton = styled.button`
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  ${({ $variant, theme }) => {
    switch ($variant) {
      case 'primary':
        return `
          background: ${theme.colors.primary};
          color: white;
          &:hover {
            background: ${theme.colors.primaryHover};
          }
        `
      case 'danger':
        return `
          background: #ef444420;
          color: #ef4444;
          &:hover {
            background: #ef444440;
          }
        `
      default:
        return `
          background: ${theme.colors.surfaceElevated};
          color: ${theme.colors.textPrimary};
          &:hover {
            background: ${theme.colors.border};
          }
        `
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const EmptyState = styled.div`
  padding: 48px 24px;
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};

  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
  }

  p {
    font-size: 1rem;
  }
`

export const LoadingState = styled.div`
  padding: 48px 24px;
  display: flex;
  justify-content: center;
  align-items: center;
`

export const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};

  button {
    padding: 8px 12px;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 6px;
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.textPrimary};
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.surfaceElevated};
      border-color: ${({ theme }) => theme.colors.primary};
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &.active {
      background: ${({ theme }) => theme.colors.primary};
      border-color: ${({ theme }) => theme.colors.primary};
      color: white;
    }
  }

  span {
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 0.9rem;
  }
`

export const PeriodButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

export const ClickableRow = styled.tr`
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceElevated};
  }
`

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`

export const Modal = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 16px;
  width: 100%;
  max-width: 700px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: ${({ theme }) => theme.colors.cardShadow};
`

export const ModalHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: space-between;

  h3 {
    font-size: 1.2rem;
    color: ${({ theme }) => theme.colors.textPrimary};
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
      color: ${({ theme }) => theme.colors.primary};
    }
  }

  button {
    background: none;
    border: none;
    color: ${({ theme }) => theme.colors.textSecondary};
    cursor: pointer;
    padding: 8px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    &:hover {
      background: ${({ theme }) => theme.colors.surfaceElevated};
      color: ${({ theme }) => theme.colors.textPrimary};
    }

    svg {
      width: 20px;
      height: 20px;
    }
  }
`

export const ModalBody = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
`

export const LogList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export const LogItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.background};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};

  .route {
    font-family: monospace;
    font-size: 0.9rem;
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  .date {
    font-size: 0.85rem;
    color: ${({ theme }) => theme.colors.textSecondary};
    white-space: nowrap;
  }
`
